import { Component, OnInit } from '@angular/core';
import { PricingNumbersService } from '../../shared/services/pricing-numbers.service';
import { Ng1IpInfoService } from '../../shared/ng1/ng1-ip-info.service';
import { Observable } from 'rxjs';
import { MsisdnRequestDto, MsisdnRequestDtoWithMetadata, OrderNumberRequest } from '../pricing-numbers/pricing-numbers';
import { map, tap } from 'rxjs/operators';
import { parsePhoneNumberFromString } from 'libphonenumber-js';
import { Router } from '@angular/router';
import { MsisdnService } from '../../shared/msisdn/msisdn.service';
import { Store } from '@ngrx/store';
import { IState } from '../../shared/models/state';
import { OrderInitialNumberAction } from '../pricing-numbers/pricing-numbers.action';
import { AmplitudeService } from '../../shared/services/amplitude.service';
import { AccountService } from '../../shared/services/account.service';
import { getOrderingInitialNumberSelector } from '../pricing-numbers/pricing-numbers.reducer';

@Component({
  selector: 'cc-first-number-welcome',
  templateUrl: './first-number-welcome.component.html',
  styleUrls: ['./first-number-welcome.component.scss']
})
export class FirstNumberWelcomeComponent implements OnInit {
  public countryCode: string;
  public firstNumber$: Observable<MsisdnRequestDtoWithMetadata | null>;
  public orderingInitialNumber$: Observable<boolean>;

  constructor(
    private pricingNumbersService: PricingNumbersService,
    private ng1IpInfoService: Ng1IpInfoService,
    private router: Router,
    private msisdnService: MsisdnService,
    private store: Store<IState>,
    private amplitudeService: AmplitudeService,
    private accountService: AccountService
  ) {}

  ngOnInit() {
    this.countryCode = this.ng1IpInfoService.getCountry();
    this.logAmplitude('Init');
    this.orderingInitialNumber$ = this.store.select(getOrderingInitialNumberSelector);

    this.accountService.getAccountConfig().subscribe(({ data: isFullyConfigured }) => {
      if (isFullyConfigured) {
        this.handleAlreadyConfiguredAccount();
      } else {
        this.loadNumbers();
      }
    });
  }

  private loadNumbers() {
    this.firstNumber$ = this.pricingNumbersService
      .getRandomNumberForCountry(this.countryCode)
      .pipe(tap(number => this.handleNoNumber(number)), map(number => this.addMetadata(number)));
  }

  private addMetadata(number: MsisdnRequestDto | null): MsisdnRequestDtoWithMetadata | null {
    return (
      number && {
        ...number,
        countryCode: this.getNumbersCountry(number.selectedMsisdnDisplayedValue),
        formatted: this.getNumberFormat(number.selectedMsisdnDisplayedValue)
      }
    );
  }

  private getNumbersCountry(phoneNumber: string) {
    const parsedNumber = phoneNumber && parsePhoneNumberFromString(phoneNumber);
    return parsedNumber && parsedNumber.country.toLowerCase();
  }

  private getNumberFormat(phoneNumber: string) {
    if (this.getNumbersCountry(phoneNumber) === 'us') {
      return parsePhoneNumberFromString(phoneNumber.toString()).formatNational();
    } else {
      return parsePhoneNumberFromString(phoneNumber.toString()).formatInternational();
    }
  }

  changeNumber() {
    this.logAmplitude('Change number clicked');
    this.router.navigateByUrl('get-first-new-number');
  }

  startCalling(request: MsisdnRequestDto) {
    const { selectedMsisdnDisplayedValue: number, selectedMsisdnId: id, pricingId, label } = request;
    const order: OrderNumberRequest = { pricingId, label, selectedMsisdn: { id: id as number, number } };
    this.store.dispatch(new OrderInitialNumberAction(order));
    this.logAmplitude('Start calling', request);
  }

  private handleNoNumber(number) {
    if (!number) {
      this.logAmplitude('Cannot find initial number - use old flow');
      this.router.navigateByUrl('get-first-new-number');
    }
  }

  private logAmplitude(message: string, event?: any) {
    this.amplitudeService.logEvent(`Panel:Welcome:Initial number: ${message}`, { code: this.countryCode, ...event });
  }

  private handleAlreadyConfiguredAccount() {
    this.logAmplitude('Already fully configured');
    this.router.navigateByUrl('/');
  }
}
